import { faAngleRight, faRotate, faSpinner, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faBellConcierge, faFileInvoice } from '@fortawesome/pro-light-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecurringSeriesCreated } from '@spike/new-booking-model';
import { RecurringSeriesState, RecurringSeriesStatus, createRecurringSeriesThunk } from '@spike/new-bookings-action';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import SidePanel from 'components/UI/SidePanel';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { Moment } from 'moment-timezone';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { AppointmentMultiple } from 'model/NewMultipleBooking';
import {
    ConflictPreference,
    RecurrenceEnd,
    RecurrenceFrequencyOption,
    RecurringSeriesConfig
} from 'model/RecurringSeries';
import { RecurringSeriesTemplate, buildRecurringSeriesRequest, getSeriesAnchorDate } from '../utils/recurringSeries';
import ConflictsStep from './Steps/ConflictsStep';
import EndsStep from './Steps/EndsStep';
import RepeatStep from './Steps/RepeatStep';
import SummaryStep from './Steps/SummaryStep';
import CreatedSeriesView from './CreatedSeriesView';
import { RECURRING_PANEL_WIDTH, useDockedPanelStyles } from './panelDocking';
import { useRecurringSeriesCreationProgress } from './useRecurringSeriesCreationProgress';
import { useRecurringSeriesPreview } from './useRecurringSeriesPreview';
import styles from './RecurringSeriesPanel.module.scss';

interface RecurringSeriesPanelProps {
    config: RecurringSeriesConfig;
    template: RecurringSeriesTemplate | undefined;
    multipleAppointments: Array<AppointmentMultiple>;
    bookingDisabled: boolean;
    inline?: boolean;
    onChange: (config: RecurringSeriesConfig) => void;
    onClose: () => void;
    onSeriesCreated: (created: RecurringSeriesCreated) => void;
}

const PANEL_LEAVE_MS = 200;

export const RecurringSeriesPanel: FunctionComponent<RecurringSeriesPanelProps> = props => {
    const inline = props.inline ?? false;
    const classes = useDockedPanelStyles(!inline);
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();
    const [isLeaving, setIsLeaving] = useState(false);
    const leaveTimeout = useRef<number>();
    const [retrySource, setRetrySource] = useState<RecurringSeriesCreated | undefined>(undefined);

    const recurringSeries = useSelector<RootState, RecurringSeriesState>(state => state.newBookings.recurringSeries);

    useEffect(() => () => window.clearTimeout(leaveTimeout.current), []);

    const closeHandler = useCallback(() => {
        if (inline) {
            props.onClose();
            return;
        }

        setIsLeaving(true);
        leaveTimeout.current = window.setTimeout(props.onClose, PANEL_LEAVE_MS);
    }, [inline, props.onClose]);

    const anchorDate: Moment | undefined = useMemo(
        () => getSeriesAnchorDate(props.multipleAppointments),
        [props.multipleAppointments]
    );

    const request = useMemo(
        () => buildRecurringSeriesRequest(props.template, props.config, anchorDate),
        [props.template, props.config, anchorDate]
    );

    const isCreating = recurringSeries.status === RecurringSeriesStatus.Creating;
    const created =
        recurringSeries.status === RecurringSeriesStatus.CreateSuccess ? recurringSeries.created : undefined;
    const createError = isCreating || created ? undefined : recurringSeries.createError;
    const hasRejectedPreview =
        recurringSeries.status === RecurringSeriesStatus.PreviewError && recurringSeries.previewError !== undefined;

    useRecurringSeriesPreview(isCreating || created ? undefined : request);

    const phase = useRecurringSeriesCreationProgress(created?.series.uuid, created?.series.status);

    useEffect(() => {
        if (!isCreating) {
            setRetrySource(undefined);
        }
    }, [isCreating]);

    useEffect(() => {
        if (created) {
            props.onSeriesCreated(created);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [created]);

    const submitDisabled = props.bookingDisabled || !request || isCreating || hasRejectedPreview;

    const changeFrequency = (frequency: RecurrenceFrequencyOption | undefined) =>
        props.onChange({ ...props.config, frequency });

    const changeEnd = (end: RecurrenceEnd) => props.onChange({ ...props.config, end });

    const changeConflicts = (conflicts: ConflictPreference) => props.onChange({ ...props.config, conflicts });

    const handleSubmit = () => {
        if (request) {
            dispatch(createRecurringSeriesThunk(apiClientWrapper, request));
        }
    };

    const handleRetry = () => {
        if (request && created) {
            setRetrySource(created);
            dispatch(createRecurringSeriesThunk(apiClientWrapper, request));
        }
    };

    const renderHeader = (title: string, intro?: string) => (
        <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button
                type="button"
                className={styles.collapseButton}
                onClick={closeHandler}
            >
                <FontAwesomeIcon icon={faAngleRight} />
            </button>
            {intro && <p className={styles.intro}>{intro}</p>}
        </div>
    );

    const renderStats = (total: number) => (
        <div className={styles.stats}>
            <div className={styles.stat}>
                <div className={clsx(styles.statIcon, styles.statIconOrders)}>
                    <FontAwesomeIcon icon={faBellConcierge} />
                </div>
                <span className={styles.statValue}>{total}</span>
                <span className={styles.statLabel}>Orders</span>
            </div>
            <div className={styles.stat}>
                <div className={clsx(styles.statIcon, styles.statIconInvoices)}>
                    <FontAwesomeIcon icon={faFileInvoice} />
                </div>
                <span className={styles.statValue}>{total}</span>
                <span className={styles.statLabel}>Invoices</span>
            </div>
        </div>
    );

    const renderNotice = (text: string) => (
        <div className={styles.notice}>
            <FontAwesomeIcon
                icon={faCircleInfo}
                className={styles.noticeIcon}
            />
            <p className={styles.noticeText}>{text}</p>
        </div>
    );

    const renderCreating = (submitted: RecurringSeriesCreated) => (
        <>
            {renderHeader('Creating Recurring Bookings')}

            <div className={styles.submitted}>
                <div className={styles.spinnerCircle}>
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className={styles.spinnerIcon}
                    />
                </div>
                <p className={styles.submittedTitle}>Your recurring bookings being created</p>
                <p className={styles.submittedText}>
                    You&apos;ll receive a notification when they&apos;re ready so you can review them.
                </p>

                {renderStats(submitted.series.progress.total)}

                {renderNotice(
                    'Availability, staff schedule, and business closures will be checked when the series is created.'
                )}
            </div>

            <div className={styles.footer}>
                <Button
                    variant="black"
                    className={styles.submitButton}
                    label="Close"
                    onClick={closeHandler}
                />
            </div>
        </>
    );

    const renderFailed = (failed: RecurringSeriesCreated) => (
        <>
            {renderHeader('Recurring Bookings')}

            <div className={styles.submitted}>
                <div className={styles.spinnerCircle}>
                    <FontAwesomeIcon
                        icon={faXmark}
                        className={styles.failedIcon}
                    />
                </div>
                <p className={styles.submittedTitle}>We couldn&apos;t create your bookings</p>
                <p className={styles.submittedText}>
                    Something went wrong while processing your recurring bookings. Please try again.
                </p>

                {renderStats(failed.series.progress.total)}

                {renderNotice('No bookings or invoices were created. Your previous bookings are not affected.')}
            </div>

            <div className={styles.footer}>
                <Button
                    variant="black"
                    disabled={!request}
                    className={styles.submitButton}
                    label="Try Again"
                    onClick={handleRetry}
                />
            </div>
        </>
    );

    const renderEmpty = () => (
        <>
            {renderHeader('Create Recurring Booking')}

            <div className={styles.submitted}>
                <div className={styles.spinnerCircle}>
                    <FontAwesomeIcon
                        icon={faRotate}
                        className={styles.emptyIcon}
                    />
                </div>
                <p className={clsx(styles.submittedTitle, styles.emptyTitle)}>Start by choosing a client</p>
                <p className={clsx(styles.submittedText, styles.emptyText)}>
                    Select a client, a pet, and at least one service to create a recurring booking.
                </p>
            </div>

            <div className={styles.footer}>
                <Button
                    variant="black"
                    className={styles.submitButton}
                    label="Close"
                    onClick={closeHandler}
                />
            </div>
        </>
    );

    const renderConfiguration = () => (
        <>
            {renderHeader(
                'Create Recurring Booking',
                'This template booking will be repeated as separate bookings. Each occurrence will create its own invoice.'
            )}

            <div className={styles.body}>
                <RepeatStep
                    frequency={props.config.frequency}
                    onChangeFrequency={changeFrequency}
                />
                {props.config.frequency && (
                    <>
                        <EndsStep
                            end={props.config.end}
                            timeZone={timeZone}
                            anchorDate={anchorDate}
                            onChangeEnd={changeEnd}
                        />
                        <ConflictsStep
                            conflicts={props.config.conflicts}
                            onChangeConflicts={changeConflicts}
                        />
                        <SummaryStep
                            hasRequest={request !== undefined}
                            status={recurringSeries.status}
                            preview={recurringSeries.preview}
                            error={recurringSeries.previewError}
                            requestedCount={props.config.end.type === 'after' ? props.config.end.bookings : undefined}
                        />
                    </>
                )}
            </div>

            <div className={styles.footer}>
                {createError && <p className={styles.footerError}>{createError}</p>}
                <Button
                    variant="green"
                    disabled={submitDisabled}
                    isLoading={isCreating}
                    className={styles.submitButton}
                    label="Create Recurring Booking"
                    onClick={handleSubmit}
                />
            </div>
        </>
    );

    const renderState = () => {
        if (created && phase === 'created') {
            return (
                <CreatedSeriesView
                    recurringUuid={created.series.uuid}
                    onClose={closeHandler}
                />
            );
        }

        if (created && phase === 'failed') {
            return renderFailed(created);
        }

        const submitted = created ?? retrySource;
        if (submitted) {
            return renderCreating(submitted);
        }

        return props.template ? renderConfiguration() : renderEmpty();
    };

    const content = <div className={styles.wrapper}>{renderState()}</div>;

    if (inline) {
        return <section className={styles.inline}>{content}</section>;
    }

    return (
        <SidePanel
            side="right"
            hideBackdrop
            disableEnforceFocus
            zIndex={1000}
            width={RECURRING_PANEL_WIDTH}
            classesInner={{
                root: styles.root,
                paper: clsx(styles.paper, classes.paper, { [styles.paperLeaving]: isLeaving })
            }}
            classesBackdrop={{ classes: { root: styles.backdrop } }}
            onClose={closeHandler}
        >
            {content}
        </SidePanel>
    );
};

export default RecurringSeriesPanel;
